import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../../../Config/axiosInstance";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import "./FormOfferRegistry.css";
import DatePicker from "react-datepicker";
import { ro } from "date-fns/locale";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";

const animatedComponents = makeAnimated();

registerLocale("ro", ro);

const formatDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  const date = new Date(year, month - 1, day);
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const formatAmountDisplay = (value) => {
  const n = Number(value);

  if (!Number.isFinite(n)) return "0,00";

  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const selectPortalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
  menu: (base) => ({ ...base, zIndex: 999999 }),
};

const AddOfferRegistry = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState(null);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [isOppLoading, setIsOppLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selClientId, setSelClientId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selOfferDate, setSelOfferDate] = useState("");
  const [selStatus, setSelStatus] = useState(null);
  const [selValidity, setSelValidity] = useState(null);
  const [selDescription, setSelDescription] = useState("");
  const [selectedOpps, setSelectedOpps] = useState([]);
  const [lines, setLines] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clientContacts, setClientContacts] = useState([]);
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const [selOfferObject, setSelOfferObject] = useState("");
  const [selExecutionTerm, setSelExecutionTerm] = useState("");

  const [customFields, setCustomFields] = useState([]);
  const [files, setFiles] = useState([]);
  const [dropzoneKey, setDropzoneKey] = useState(Date.now());
  const [errorNotification, setErrorNotification] = useState("");

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    opportunities: true,
    additionalFields: true,
    attachments: true,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const showError = useCallback((message, timeout = 5000) => {
    setErrorNotification(message);
    setTimeout(() => setErrorNotification(""), timeout);
  }, []);

  const exchangeOptionsByCurrencyId = useMemo(() => {
    const m = new Map();

    for (const r of exchangeRates) {
      const curId = r.currency;
      if (!m.has(curId)) m.set(curId, []);

      m.get(curId).push({
        value: r.id,
        label: `${r.value} (${formatDate(r.date)})`,
        shortLabel: `${r.value}`,
        rateValue: Number(r.value),
        rateDate: r.date,
      });
    }

    for (const [k, arr] of m.entries()) {
      arr.sort((a, b) => new Date(b.rateDate) - new Date(a.rateDate));
      m.set(k, arr);
    }

    return m;
  }, [exchangeRates]);

  const liveTotalDisplay = useMemo(() => {
    if (!lines.length) return "0,00";

    const validLines = lines.filter((line) => {
      const hasSum =
        line.sum !== null && line.sum !== undefined && line.sum !== "";
      const hasCurrency = !!line.currency?.value;
      return hasSum && hasCurrency;
    });

    if (!validLines.length) return "0,00";

    const distinctCurrencyIds = [
      ...new Set(validLines.map((line) => line.currency.value)),
    ];

    if (distinctCurrencyIds.length === 1) {
      const total = validLines.reduce((acc, line) => {
        const val = Number(line.sum);
        return acc + (Number.isFinite(val) ? val : 0);
      }, 0);

      const currencyLabel = validLines[0]?.currency?.label || "";
      return `${formatAmountDisplay(total)} ${currencyLabel}`.trim();
    }

    const totalSystem = validLines.reduce((acc, line) => {
      const val = Number(line.calculated);
      return acc + (Number.isFinite(val) ? val : 0);
    }, 0);

    return `${formatAmountDisplay(totalSystem)} ${systemCurrencyLabel || ""}`.trim();
  }, [lines, systemCurrencyLabel]);

  const getDefaultExchangeRateOption = (currencyId) => {
    const arr = exchangeOptionsByCurrencyId.get(currencyId) || [];
    return arr.length ? arr[0] : null;
  };

  const computeSystemAmount = (sum, currencyId, exchangeOpt) => {
    if (sum == null || sum === "") return "";

    const s = Number(sum);
    if (!Number.isFinite(s)) return "";

    if (!currencyId || !systemCurrencyId) return "";

    if (Number(currencyId) === Number(systemCurrencyId)) return s.toFixed(2);

    const rateVal = exchangeOpt?.rateValue;
    if (!rateVal || !Number.isFinite(rateVal)) return "";

    return (s * rateVal).toFixed(2);
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);

      try {
        const clientsRes = await axiosInstance.get("/clients/");
        setClients(
          (clientsRes.data || []).map((c) => ({
            value: c.id,
            label: c.name,
          }))
        );

        const curRes = await axiosInstance.get("/currencies/");
        const curOpts = (curRes.data || []).map((c) => ({
          value: c.id,
          label: c.abbreviation,
        }));
        setCurrencies(curOpts);

        const sysRes = await axiosInstance.get("/system-currency/1/");
        const sysCurrencyId = sysRes.data?.currency ?? null;
        const sysCurrencyLabel = sysRes.data?.currency_label ?? null;

        setSystemCurrencyId(sysCurrencyId);
        setSystemCurrencyLabel(sysCurrencyLabel);

        const exRes = await axiosInstance.get("/exchange-rate/");
        setExchangeRates(
          (exRes.data || []).sort(
            (a, b) => new Date(b.date) - new Date(a.date)
          )
        );
      } catch (e) {
        console.error("fetch error", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchOpp = async () => {
      if (!selClientId) {
        setOpportunities([]);
        setSelectedOpps([]);
        setLines([]);
        setClientContacts([]);
        setSelectedContactPerson(null);
        return;
      }

      setIsOppLoading(true);

      try {
        const res = await axiosInstance.get(
          `/opportunities/?client=${selClientId}&available_for_offer=1`
        );

        const opts = (res.data || []).map((opp) => ({
          value: opp.id,
          label: `${opp.code_opportunity} - ${opp.title_opportunity}`,
          project_code_text: opp.project_code_label ?? "",
          subproject_code_text: opp.subproject_code_label ?? "",
        }));

        const contactsRes = await axiosInstance.get(
          `/client-contacts/?client=${selClientId}`
        );

        const contactOpts = (contactsRes.data || []).map((contact) => ({
          value: contact.id,
          label: `${contact.name}${contact.position ? ` - ${contact.position}` : ""}`,
        }));

        if (!cancelled) {
          setOpportunities(opts);
          setSelectedOpps([]);
          setLines([]);
          setClientContacts(contactOpts);
          setSelectedContactPerson(null);
        }
      } catch (e) {
        console.error("opp fetch error", e);

        if (!cancelled) {
          setOpportunities([]);
          setSelectedOpps([]);
          setLines([]);
          setClientContacts([]);
          setSelectedContactPerson(null);
        }
      } finally {
        if (!cancelled) setIsOppLoading(false);
      }
    };

    fetchOpp();

    return () => {
      cancelled = true;
    };
  }, [selClientId]);

  const handleClientChange = (opt) => {
    setSelectedClient(opt);
    const newId = opt ? opt.value : null;
    setSelClientId(newId);
    setSelectedOpps([]);
    setLines([]);
    setClientContacts([]);
    setSelectedContactPerson(null);

    setFieldErrors((prev) => ({
      ...prev,
      selClientId: "",
      lines: "",
    }));
  };

  const handleDateChange = (date) => {
    const iso = date ? date.toISOString().split("T")[0] : "";
    setSelectedDate(date);
    setSelOfferDate(iso);

    if (fieldErrors.selOfferDate) {
      setFieldErrors((prev) => ({
        ...prev,
        selOfferDate: "",
      }));
    }
  };

  const handleOppsMultiChange = (opts) => {
    const selected = opts || [];
    setSelectedOpps(selected);

    setLines((prevLines) => {
      const prevById = new Map(prevLines.map((l) => [l.opportunityId, l]));
      const next = [];

      for (const o of selected) {
        const existing = prevById.get(o.value);

        if (existing) {
          next.push(existing);
        } else {
          next.push({
            opportunityId: o.value,
            opportunityLabel: o.label,
            project_code_text: o.project_code_text || "",
            subproject_code_text: o.subproject_code_text || "",
            sum: null,
            currency: null,
            exchange_rate: null,
            calculated: "",
          });
        }
      }

      return next;
    });

    if (fieldErrors.lines) {
      setFieldErrors((prev) => ({
        ...prev,
        lines: "",
      }));
    }
  };

  const updateLine = (opportunityId, patch) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.opportunityId !== opportunityId) return l;

        const next = { ...l, ...patch };
        const currencyId = next.currency?.value ?? null;

        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId)
        ) {
          if (!next.exchange_rate) {
            next.exchange_rate = getDefaultExchangeRateOption(currencyId);
          }
        } else {
          next.exchange_rate = null;
        }

        next.calculated = computeSystemAmount(
          next.sum,
          currencyId,
          next.exchange_rate
        );

        return next;
      })
    );
  };

  const removeOpportunity = (opportunityId) => {
    setSelectedOpps((prev) => prev.filter((o) => o.value !== opportunityId));
    setLines((prev) => prev.filter((l) => l.opportunityId !== opportunityId));
  };

  const addCustomField = () => {
    setCustomFields((prev) => [
      ...prev,
      {
        localId: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        field_title: "",
        field_content: "",
      },
    ]);

    if (fieldErrors.custom_fields) {
      setFieldErrors((prev) => ({
        ...prev,
        custom_fields: "",
      }));
    }
  };

  const updateCustomField = (localId, patch) => {
    setCustomFields((prev) =>
      prev.map((field) =>
        field.localId === localId ? { ...field, ...patch } : field
      )
    );

    if (fieldErrors.custom_fields) {
      setFieldErrors((prev) => ({
        ...prev,
        custom_fields: "",
      }));
    }
  };

  const removeCustomField = (localId) => {
    setCustomFields((prev) =>
      prev.filter((field) => field.localId !== localId)
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [],
      "application/msword": [],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [],
      "image/jpeg": [],
      "image/png": [],
      "application/vnd.ms-excel": [],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      "text/plain": [],
    },
    multiple: true,
    maxSize: 1024 * 1024 * 100,
    onDrop: (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const tooLarge = rejectedFiles.filter(
          (f) => f.errors[0]?.code === "file-too-large"
        );
        if (tooLarge.length > 0) {
          showError(`File ${tooLarge[0].file.name} exceeds 100MB.`, 3000);
        }
      }

      if (acceptedFiles.length > 0) {
        setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
      }
    },
  });

  const handleRemoveFile = (indexToRemove) => {
    setFiles((prevFiles) => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);
      if (updatedFiles.length === 0) {
        setDropzoneKey(Date.now());
      }
      return updatedFiles;
    });
  };

  const handleFileClick = (file) => {
    const url = URL.createObjectURL(file);

    if (file.type === "application/pdf") {
      const newWindow = window.open();
      newWindow.document.write(`
        <html>
          <head>
            <title>${file.name}</title>
            <style>
              body { margin: 0; height: 100vh; }
              embed { width: 100%; height: 100%; }
            </style>
          </head>
          <body>
            <embed src="${url}" type="application/pdf" />
          </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 100);
    }
  };

  const getFileIcon = (file) => {
    const fileType = file.type;

    if (fileType === "application/pdf") {
      return <PictureAsPdfIcon style={{ fontSize: 24, color: "#d32f2f" }} />;
    } else if (
      fileType === "application/msword" ||
      fileType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#1976d2" }} />;
    } else if (
      fileType === "application/vnd.ms-excel" ||
      fileType ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      return <DescriptionIcon style={{ fontSize: 24, color: "#2e7d32" }} />;
    } else if (fileType.startsWith("image/")) {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#9c27b0" }} />;
    } else {
      return <InsertDriveFileIcon style={{ fontSize: 24, color: "#757575" }} />;
    }
  };

  const validateFiles = () => {
    if (files.length === 0) return true;

    const MAX_FILE_SIZE = 100 * 1024 * 1024;
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      showError(
        `The following files exceed the 100MB limit:\n${oversizedFiles
          .map((f) => f.name)
          .join("\n")}`,
        5000
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    if (!validateFiles()) return;

    setIsSubmitting(true);

    const newErrors = {};

    if (!selOfferDate) newErrors.selOfferDate = "Required";
    if (!selClientId) newErrors.selClientId = "Required";
    if (!selDescription) newErrors.selDescription = "Required";
    if (!selectedContactPerson) newErrors.selectedContactPerson = "Required";
    if (!selOfferObject.trim()) newErrors.selOfferObject = "Required";
    if (!selExecutionTerm.trim())
      newErrors.selExecutionTerm = "Required";
    if (!lines.length) newErrors.lines = "Select at least one opportunity";

    lines.forEach((l, idx) => {
      if (l.sum != null && l.sum !== "") {
        if (!l.currency) {
          newErrors[`line_${idx}_currency`] = "Currency required when sum is set";
        }

        const currencyId = l.currency?.value ?? null;
        if (
          currencyId &&
          systemCurrencyId &&
          Number(currencyId) !== Number(systemCurrencyId) &&
          !l.exchange_rate
        ) {
          newErrors[`line_${idx}_exchange_rate`] = "Exchange rate required";
        }
      }
    });

    customFields.forEach((field, idx) => {
      const title = (field.field_title || "").trim();
      const content = (field.field_content || "").trim();

      if (content && !title) {
        newErrors[`custom_field_${idx}_title`] = "Field title is required";
      }
    });

    setFieldErrors(newErrors);

    if (Object.keys(newErrors).length) {
      setIsSubmitting(false);
      const firstError = document.querySelector(".FormOfferRegistry-error");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      const submissionData = new FormData();

      submissionData.append("offer_date", selOfferDate);
      submissionData.append("client", String(selClientId || ""));
      submissionData.append("status", selStatus ? selStatus.value : "");
      submissionData.append("valability", selValidity ? selValidity.value : "");
      submissionData.append("description", selDescription);
      submissionData.append("offer_object", selOfferObject);
      submissionData.append("execution_term", selExecutionTerm);

      if (selectedContactPerson) {
        submissionData.append(
          "contact_persons",
          JSON.stringify([selectedContactPerson.value])
        );
      }

      const offerLinesPayload = lines.map((line) => ({
        opportunity: Number(line.opportunityId),
        sum: line.sum === null || line.sum === "" ? null : Number(line.sum),
        currency: line.currency ? Number(line.currency.value) : null,
        exchange_rate: line.exchange_rate ? Number(line.exchange_rate.value) : null,
      }));

      submissionData.append(
        "offer_opportunities",
        JSON.stringify(offerLinesPayload)
      );

      const customFieldsPayload = customFields
        .map((field, index) => ({
          field_title: (field.field_title || "").trim(),
          field_content: field.field_content || "",
          order: index,
        }))
        .filter((field) => field.field_title || field.field_content);

      submissionData.append(
        "custom_fields",
        JSON.stringify(customFieldsPayload)
      );

      if (files.length > 0) {
        files.forEach((file) => {
          submissionData.append("attachments", file);
        });
      }

      await axiosInstance.post("/offer-register/", submissionData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      navigate(-1);
    } catch (error) {
      console.error("Save error:", error.response?.data || error.message);

      if (error.response?.data) {
        const err = error.response.data;

        if (
          err.attachments ||
          (typeof err === "object" &&
            JSON.stringify(err).toLowerCase().includes("file"))
        ) {
          showError("File upload error. Please check file type and size.");
        } else if (typeof err === "object") {
          const firstMessage =
            Object.values(err).flat().join(" | ") || JSON.stringify(err);
          showError(firstMessage);
        } else {
          showError("An error occurred while saving.");
        }
      } else {
        showError("An error occurred while saving.");
      }

      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(false);
  };

  const ErrorMessage = ({ error }) =>
    error ? (
      <div className="FormOfferRegistry-error">
        <span className="error-icon">⚠️</span>
        <span>{error}</span>
      </div>
    ) : null;

  const SectionHeader = ({ title, section }) => (
    <div
      className="FormOfferRegistry-section-header"
      onClick={() => toggleSection(section)}
    >
      <h2>{title}</h2>
      <button type="button" className="toggle-btn">
        {expandedSections[section] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="FormOfferRegistry">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading offer form...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="FormOfferRegistry">
      {errorNotification && (
        <div className="FormOfferRegistry-top-error">{errorNotification}</div>
      )}

      <div className="FormOfferRegistry-header">
        <ArrowBackIcon className="back-arrow" onClick={() => navigate(-1)} />
        <h1 className="header-title">ADD OFFER </h1>
      </div>

      <hr className="title-separator" />

      <form onSubmit={handleSubmit} className="FormOfferRegistry-form">
        <div className="FormOfferRegistry-section">
          <SectionHeader title="BASIC INFORMATION" section="basic" />

          {expandedSections.basic && (
            <div className="section-content">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Client <span className="required">*</span>
                  </label>

                  <Select
                    options={clients}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selectedClient}
                    onChange={handleClientChange}
                    placeholder="Select client..."
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />

                  <ErrorMessage error={fieldErrors.selClientId} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Offer Date <span className="required">*</span>
                  </label>

                  <div style={{ position: "relative" }}>
                    <DatePicker
                      selected={selectedDate}
                      onChange={handleDateChange}
                      dateFormat="dd.MM.yyyy"
                      locale="ro"
                      placeholderText="Select date"
                      className="FormOfferRegistry-date-input"
                      popperContainer={({ children }) => (
                        <div style={{ position: "fixed", zIndex: 9999 }}>
                          {children}
                        </div>
                      )}
                    />

                    <span
                      className="calendar-icon"
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </span>
                  </div>

                  <ErrorMessage error={fieldErrors.selOfferDate} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    Contact Person <span className="required">*</span>
                  </label>
                  <Select
                    options={clientContacts}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selectedContactPerson}
                    onChange={(opt) => {
                      setSelectedContactPerson(opt);
                      if (fieldErrors.selectedContactPerson) {
                        setFieldErrors((prev) => ({
                          ...prev,
                          selectedContactPerson: "",
                        }));
                      }
                    }}
                    placeholder={
                      !selClientId
                        ? "Select a client first"
                        : "Select contact person..."
                    }
                    components={animatedComponents}
                    isDisabled={!selClientId}
                    isClearable
                    noOptionsMessage={() => "No contacts found"}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                  <ErrorMessage error={fieldErrors.selectedContactPerson} />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Execution Term <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="FormOfferRegistry-date-input"
                    value={selExecutionTerm}
                    onChange={(e) => {
                      setSelExecutionTerm(e.target.value);
                    }}
                    placeholder="Enter execution term..."
                  />
                  <ErrorMessage error={fieldErrors.selExecutionTerm} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status</label>

                  <Select
                    options={[
                      {
                        value: "Under client review",
                        label: "Under client review",
                      },
                      { value: "Contract signed", label: "Contract signed" },
                      { value: "On hold", label: "On hold" },
                      { value: "Offer closed", label: "Offer closed" },
                    ]}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selStatus}
                    onChange={setSelStatus}
                    placeholder="Select status"
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Validity (days)</label>

                  <Select
                    options={[
                      { value: "10", label: "10 days" },
                      { value: "30", label: "30 days" },
                      { value: "60", label: "60 days" },
                      { value: "90", label: "90 days" },
                    ]}
                    classNamePrefix="FormOfferRegistry-select"
                    value={selValidity}
                    onChange={setSelValidity}
                    placeholder="Select validity"
                    components={animatedComponents}
                    isClearable
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={selectPortalStyles}
                  />
                </div>
              </div>

              <div className="form-group full-width" style={{ marginBottom: "10px" }}>
                <label className="form-label">
                  Offer Object <span className="required">*</span>
                </label>

                <input
                  type="text"
                  className="FormOfferRegistry-date-input"
                  value={selOfferObject}
                  onChange={(e) => {
                    setSelOfferObject(e.target.value);
                    if (fieldErrors.selOfferObject) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        selOfferObject: "",
                      }));
                    }
                  }}
                  placeholder="Enter offer object..."
                />

                <ErrorMessage error={fieldErrors.selOfferObject} />
              </div>

              <div className="form-group full-width">
                <label className="form-label">
                  Description <span className="required">*</span>
                </label>

                <textarea
                  className="FormOfferRegistry-description"
                  value={selDescription}
                  onChange={(e) => {
                    setSelDescription(e.target.value);
                    if (fieldErrors.selDescription) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        selDescription: "",
                      }));
                    }
                  }}
                  placeholder="Enter offer description, terms, conditions, or any additional notes..."
                  rows={3}
                />

                <ErrorMessage error={fieldErrors.selDescription} />
              </div>
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="OPPORTUNITIES & PRICING" section="opportunities" />

          {expandedSections.opportunities && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">
                  Select Opportunities <span className="required">*</span>
                </label>

                <Select
                  isMulti
                  options={opportunities}
                  classNamePrefix="FormOfferRegistry-select"
                  value={selectedOpps}
                  onChange={handleOppsMultiChange}
                  placeholder={
                    !selClientId
                      ? "Select a client first"
                      : isOppLoading
                        ? "Loading opportunities..."
                        : "Search and select opportunities..."
                  }
                  components={animatedComponents}
                  isDisabled={!selClientId || isOppLoading}
                  noOptionsMessage={() => "No opportunities found"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectPortalStyles}
                />

                <ErrorMessage error={fieldErrors.lines} />
              </div>

              {lines.length === 0 ? (
                <div className="no-opportunities-message">
                  <AddIcon className="hint-icon" />
                  <span>No opportunities selected yet</span>
                </div>
              ) : (
                <div className="opportunities-grid">
                  {lines.map((line, idx) => {
                    const currencyId = line.currency?.value ?? null;
                    const exchangeOpts = currencyId
                      ? exchangeOptionsByCurrencyId.get(currencyId) || []
                      : [];

                    return (
                      <div key={line.opportunityId} className="opportunity-item">
                        <div className="opportunity-item-header">
                          <div className="item-title">
                            <h3>{line.opportunityLabel}</h3>
                          </div>

                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => removeOpportunity(line.opportunityId)}
                            title="Remove opportunity"
                          >
                            <DeleteIcon />
                          </button>
                        </div>

                        <div className="opportunity-details">
                          <div className="detail-row">
                            <div className="detail-field">
                              <label className="form-label">Project Code</label>
                              <input
                                type="text"
                                value={line.project_code_text}
                                readOnly
                                className="readonly-field"
                              />
                            </div>

                            <div className="detail-field">
                              <label className="form-label">Subproject Code</label>
                              <input
                                type="text"
                                value={line.subproject_code_text}
                                readOnly
                                className="readonly-field"
                              />
                            </div>
                          </div>

                          <div className="pricing-row">
                            <div className="pricing-field">
                              <label className="form-label">Value (Original currency)</label>

                              <NumericFormat
                                value={line.sum}
                                onValueChange={(values) => {
                                  const v =
                                    values.floatValue !== undefined
                                      ? values.floatValue
                                      : null;
                                  updateLine(line.opportunityId, { sum: v });
                                }}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                placeholder="0,00"
                                className="amount-input"
                              />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Currency</label>

                              <Select
                                options={currencies}
                                classNamePrefix="FormOfferRegistry-select"
                                value={line.currency}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, { currency: opt })
                                }
                                placeholder="Select"
                                components={animatedComponents}
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                              />

                              <ErrorMessage error={fieldErrors[`line_${idx}_currency`]} />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">Exchange Rate</label>

                              <Select
                                options={exchangeOpts}
                                classNamePrefix="FormOfferRegistry-select"
                                value={line.exchange_rate}
                                onChange={(opt) =>
                                  updateLine(line.opportunityId, {
                                    exchange_rate: opt,
                                  })
                                }
                                formatOptionLabel={(option, { context }) =>
                                  context === "value"
                                    ? option.shortLabel || option.label
                                    : option.label
                                }
                                placeholder={
                                  !line.currency
                                    ? "Select currency first"
                                    : Number(currencyId) === Number(systemCurrencyId)
                                      ? "Not needed"
                                      : exchangeOpts.length
                                        ? "Select rate"
                                        : "No rates"
                                }
                                components={animatedComponents}
                                isClearable
                                isDisabled={
                                  !line.currency ||
                                  Number(currencyId) === Number(systemCurrencyId)
                                }
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                styles={selectPortalStyles}
                              />

                              <ErrorMessage
                                error={fieldErrors[`line_${idx}_exchange_rate`]}
                              />
                            </div>

                            <div className="pricing-field">
                              <label className="form-label">
                                Value
                                {systemCurrencyLabel
                                  ? ` (${systemCurrencyLabel})`
                                  : ""}
                              </label>

                              <NumericFormat
                                value={line.calculated}
                                thousandSeparator="."
                                decimalSeparator=","
                                decimalScale={2}
                                fixedDecimalScale
                                placeholder="0,00"
                                className="amount-input"
                                readOnly
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="ADDITIONAL FIELDS" section="additionalFields" />

          {expandedSections.additionalFields && (
            <div className="section-content">
              <div className="form-group full-width">
                <div className="additional-fields-header">
                  <div className="additional-fields-info">
                    <p className="additional-fields-subtitle">
                      Add optional custom fields that will be saved together with this offer.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="additional-fields-add-btn"
                    onClick={addCustomField}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <AddIcon fontSize="small" />
                    Add Field
                  </button>
                </div>

                {customFields.length === 0 ? (
                  <div className="additional-fields-empty">
                    No additional fields added.
                  </div>
                ) : (
                  <div className="additional-fields-list">
                    {customFields.map((field, index) => (
                      <div key={field.localId} className="additional-field-card">
                        <div className="additional-field-row">
                          <div className="form-group">
                            <label className="form-label">Field Title</label>
                            <input
                              type="text"
                              className="FormOfferRegistry-input"
                              placeholder="Enter field title"
                              value={field.field_title}
                              onChange={(e) =>
                                updateCustomField(field.localId, {
                                  field_title: e.target.value,
                                })
                              }
                            />
                            <ErrorMessage error={fieldErrors[`custom_field_${index}_title`]} />
                          </div>

                          <div className="form-group">
                            <label className="form-label">Field Content</label>
                            <input
                              type="text"
                              className="FormOfferRegistry-input additional-field-content"
                              placeholder="Enter field content"
                              value={field.field_content}
                              onChange={(e) =>
                                updateCustomField(field.localId, {
                                  field_content: e.target.value,
                                })
                              }
                            />
                          </div>

                          <button
                            type="button"
                            className="remove-file-button"
                            onClick={() => removeCustomField(field.localId)}
                            title="Remove field"
                            style={{ marginTop: "28px" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="FormOfferRegistry-section">
          <SectionHeader title="ATTACHMENTS" section="attachments" />

          {expandedSections.attachments && (
            <div className="section-content">
              <div className="form-group full-width">
                <label className="form-label">Attachments</label>

                <div key={dropzoneKey} className="dropzone-wrapper">
                  <div {...getRootProps({ className: "dropzone" })}>
                    <input {...getInputProps()} />
                    <div className="dropzone-content">
                      {isDragActive ? (
                        <p>Drop the files here...</p>
                      ) : (
                        <p>Drag & drop files or click to select</p>
                      )}
                      <CloudUploadIcon style={{ fontSize: 40, color: "#888" }} />
                    </div>
                  </div>

                  {files.length > 0 && (
                    <div className="files-preview-container">
                      <h4>Selected Files ({files.length})</h4>
                      <div className="files-list">
                        {files.map((file, index) => (
                          <div key={index} className="file-preview-item">
                            <div className="file-info">
                              {getFileIcon(file)}
                              <span
                                className="file-name clickable"
                                title={
                                  file.type === "application/pdf"
                                    ? "Click to preview PDF"
                                    : "Click to download file"
                                }
                                onClick={() => handleFileClick(file)}
                              >
                                {file.name.length > 40
                                  ? `${file.name.substring(0, 40)}...`
                                  : file.name}
                              </span>
                              <span className="file-size">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <button
                              type="button"
                              className="remove-file-button"
                              onClick={() => handleRemoveFile(index)}
                              title="Remove file"
                            >
                              <DeleteIcon fontSize="small" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <p className="file-hint">
                  Accepted file types: PDF, Word, Excel, Images, Text. Multiple files
                  allowed (max 100MB per file).
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="offer-total-preview">
          <span className="offer-total-label">Total:</span>
          <span className="offer-total-value">{liveTotalDisplay}</span>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="additional-fields-add-btn"
            onClick={() => navigate(-1)}
          >
            Cancel
          </button>

          <button type="submit" className="primary-btn" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddOfferRegistry;